import { cn } from "@/lib/utils";
import {
  PROTECTION_META,
  PROTECTION_STEPS,
  type ProtectedState,
  protectionStepIndex,
} from "@/lib/semantics";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ProtectionStepperProps {
  state: ProtectedState | string;
  className?: string;
}

/**
 * Visual stop-protection ladder. PROTECTED_LADDER_V6 has TWO stop states:
 * INITIAL (SL ratchet / cliquet) → TRAILING (armed at n AND latent ≥ n·r_trail).
 * Stops only ever advance (SPEC §7/§18). The completed + current step fill green; the
 * future step stays muted. A legacy V2 break-even state (historical positions only) is
 * not on the V6 ladder — it renders as its muted humanised label, no dots. A tooltip
 * explains the current step so a non-technical reader understands how protected the
 * position is.
 */
export default function ProtectionStepper({ state, className }: ProtectionStepperProps) {
  const idx = protectionStepIndex(state as ProtectedState);

  // Not on the V6 ladder (a legacy V2 break-even state, or an off-spec value): show its
  // humanised label (muted), never silently relabel — keeps historical rows readable.
  if (idx < 0) {
    const legacy = PROTECTION_META[state as ProtectedState];
    return (
      <span className="text-muted-foreground font-sans text-xs">
        {legacy ? legacy.short : state}
      </span>
    );
  }

  const meta = PROTECTION_META[state as ProtectedState];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn("inline-flex items-center gap-1", className)}
          aria-label={`Protection du stop : ${meta.label}`}
        >
          {PROTECTION_STEPS.map((step, i) => {
            const reached = i <= idx;
            return (
              <span key={step} className="flex items-center">
                <span
                  className={cn(
                    "size-2 rounded-full transition-colors",
                    reached ? "bg-buy" : "bg-surface-2 border-border-strong border",
                  )}
                />
                {i < PROTECTION_STEPS.length - 1 && (
                  <span
                    className={cn(
                      "h-px w-2",
                      i < idx ? "bg-buy" : "bg-border-strong",
                    )}
                  />
                )}
              </span>
            );
          })}
          <span className="text-foreground ml-1 font-sans text-xs">{meta.short}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex flex-col gap-0.5">
          <strong>{meta.label}</strong>
          <span>{meta.help}</span>
          <span className="text-muted-foreground mt-1 text-[0.65rem]">
            Échelle V6 : Initial (cliquet SL) → Trail (armé). Les stops ne s'élargissent jamais.
          </span>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
